import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/internal/Observable';
import { urlEndPoints } from '../config/end-points';
import { cycle } from '../models/cycle.model';
import { cycleCalendar } from '../models/cycleCalendar.model';
import { Profile } from '../models/profile.model';

@Injectable({
  providedIn: 'root',
})
export class CycleDal {
  constructor(private httpClient: HttpClient) {}

  getEffectif(week: number, cycleId: string): Observable<any> {
    return this.httpClient.get<any>(`${urlEndPoints.cycle}effectifOfWeek?nurseCycleId=${cycleId}&week=${week}`);
  }

  getCycleNightEffectifNeeds(cycleId: string): Observable<any> {
    return this.httpClient.get<any>(`${urlEndPoints.cycle}nightEffectifNeeds?nurseCycleId=${cycleId}`);
  }

  deleteCycle(cycle: cycle): Observable<any> {
    return this.httpClient.delete<cycle>(`${urlEndPoints.cycle}${cycle._id}`);
  }

  deleteCalendar(calendar: cycleCalendar): Observable<any> {
    return this.httpClient.delete<cycle>(`${urlEndPoints.cycle}cycleCalendar/${calendar._id}`);
  }

  updateCycleCalendar(calendar: cycleCalendar): Observable<any> {
    return this.httpClient.put<cycle>(`${urlEndPoints.cycle}cycleCalendar/${calendar._id}`, calendar);
  }

  createCycleCalendar(calendar: cycleCalendar): Observable<any> {
    return this.httpClient.post<cycle>(`${urlEndPoints.cycle}cycleCalendar`, calendar);
  }

  createCycleWeekType(weekType: any): Observable<any> {
    return this.httpClient.post<cycle>(`${urlEndPoints.cycle}cycleWeekType`, weekType);
  }

  updateCycleWeekType(weekType: any): Observable<any> {
    return this.httpClient.put<cycle>(`${urlEndPoints.cycle}updateCycleWeekType`, weekType);
  }

  getAllCycleWeekTypes(): Observable<any> {
    const endPoint = `${urlEndPoints.cycle}allCycleWeekTypes`;

    return this.httpClient.get<any>(endPoint);
  }

  getAllCycleWeekTypeNames(): Observable<any> {
    const endPoint = `${urlEndPoints.cycle}allCycleWeekTypesNames`;

    return this.httpClient.get<any>(endPoint);
  }

  editCalendarsFromWeekType(data: any): Observable<any> {
    const endPoint = `${urlEndPoints.cycle}editCalendarsFromWeekType`;

    return this.httpClient.put<any>(endPoint, data);
  }

  deleteWeekType(weekTypeId: string): Observable<any> {
    return this.httpClient.delete<any>(`${urlEndPoints.cycle}deleteWeekType/${weekTypeId}`);
  }

  getCalendarsOfCycle(_id: string, cycle: cycle, weekNumbers: number, getWeekType?: boolean): Observable<any> {
    return this.httpClient.get<any>(
      `${urlEndPoints.cycle}byProfile/${_id}?nurseCycle=${cycle._id}&week=${weekNumbers}&getWeekType=${getWeekType}`
    );
  }

  getCycles(positions?: string[], isNight?: boolean): Observable<any> {
    let endPoint = `${urlEndPoints.cycle}byPositions/`;
    if (positions) {
      endPoint = endPoint + positions.join(',');
    }
    if (isNight) {
      endPoint += '?isNight=' + isNight;
    }
    return this.httpClient.get<any>(endPoint);
  }

  getCycleById(cycleId: string): Observable<any> {
    return this.httpClient.get<any>(`${urlEndPoints.cycle}cycleById/${cycleId}`);
  }

  getWeekTypeWithCalendars(nurseIds: string[], cycleId: string): Observable<any> {
    return this.httpClient.post<any>(`${urlEndPoints.cycle}weekTypeWithCalendars?nurseCycle=${cycleId}`, {
      nurseIDs: nurseIds,
    });
  }

  getCyclesCampaign(position?: string[], isNight?: boolean, campaignId?: string): Observable<any> {
    let endPoint = `${urlEndPoints.cycle}by/`;
    if (position) {
      endPoint = endPoint + position;
    }
    if (isNight) {
      endPoint += '?isNight=' + isNight;
    }
    if (campaignId && isNight) {
      endPoint += '&campaignId=' + campaignId;
    } else if (campaignId && !isNight) {
      endPoint += '?campaignId=' + campaignId;
    }
    return this.httpClient.get<any>(endPoint);
  }

  generateCycle(startDate: any, endDate: any, cycle: cycle, keepOldCalendars: boolean): Observable<any> {
    return this.httpClient.get<any>(
      `${urlEndPoints.cycle}validate/${cycle._id}?fromDate=${startDate}&toDate=${endDate}&keepOldCalendars=${keepOldCalendars}`
    );
  }

  generateProfiledCalendars(
    startDate: any,
    endDate: any,
    cycle: cycle,
    keepOldCalendars: boolean,
    profilesIds: string[],
    startingWeekNumber?: number
  ): Observable<any> {
    return this.httpClient.post<any>(
      `${urlEndPoints.cycle}validate/${cycle._id}?fromDate=${startDate}&toDate=${endDate}&keepOldCalendars=${keepOldCalendars}&startingWeekNumber=${startingWeekNumber || ''}`,
      profilesIds
    );
  }

  checkConflicts(fromDate: string, toDate: string, cycle: cycle): Observable<any> {
    return this.httpClient.get<any>(
      `${urlEndPoints.cycle}cyclesCoflits/${cycle._id}?fromDate=${fromDate}&toDate=${toDate}`
    );
  }

  createCycle(cycle: cycle, cycleSourceId: string): Observable<cycle> {
    return this.httpClient.post<cycle>(`${urlEndPoints.cycle}?copyFrom=${cycleSourceId}`, cycle);
  }

  updateOptimCycle(cycleId: string, isNight: boolean): Observable<any> {
    return this.httpClient.post<any>(`${urlEndPoints.cycle}updateOptimCycle/${cycleId}?isNight=${isNight}`, {});
  }

  editCycle(cycle: cycle): Observable<any> {
    const ids = cycle?.profiles?.map((el: Profile) => el._id) ?? [];
    const payload = {
      profileIds: ids,
      name: cycle.name,
      seniorities: cycle.seniorities,
    };
    return this.httpClient.post<cycle>(`${urlEndPoints.cycle}editCycleProfiles/${cycle._id}`, payload);
  }

  editCycleComments(cycle: cycle): Observable<any> {
    const comments = cycle.commentsByWeek;
    return this.httpClient.post<cycle>(`${urlEndPoints.cycle}editCycleComments/${cycle._id}`, comments);
  }

  updatePeriodAvailabilityCycle(calendar: cycleCalendar, indexes: number[]): Observable<any> {
    const body = {
      calendar,
      indexes,
    };
    return this.httpClient.post<cycleCalendar>(urlEndPoints.cycle + 'cycleCalendar/updatePeriod', body);
  }

  deletePeriodCycle(calendar: cycleCalendar, indexes: number[]): Observable<any> {
    const body = {
      calendar,
      indexes,
    };
    return this.httpClient.post<cycleCalendar>(urlEndPoints.cycle + 'cycleCalendar/deletePeriod', body);
  }
}
